import cloneDeep from 'lodash-es/cloneDeep';
import uniq from 'lodash-es/uniq';
import { applyPatch } from 'fast-json-patch';
import { createSelector } from 'reselect';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { LOADING_STATES } from 'cadenza/loading-state-cover/loading-state-cover';
import { hasPrivilege, ObjectPrivilege } from 'cadenza/access-management/object-privilege';
import { registerReducer, store } from 'cadenza/store/cadenza-store';
import { executeCommand as executeCommandRequest, getWorkbookState, parseWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { getLogger } from 'cadenza/utils/logging';
import { booleanFromSessionStorage, observeStore, STATUS } from 'cadenza/store/state-utils';
import { assert } from 'cadenza/utils/custom-error';
import { resolveObjecttypeAttributes } from 'cadenza/workbook/workbook-attribute-util';
import { getPanelVisibilityFromDisplayState, initializePanelVisibility, WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { cadenzaTopWindow } from 'cadenza/utils/iframe';
import { DATA_VIEW_KEYS } from 'cadenza/workbook/workbook-panel/settings/common-data-view-settings';
import { QUERY_EXECUTION_STRATEGY_TYPE } from 'cadenza/workbook/query-execution-strategy-type';
import { ViewType } from 'cadenza/workbook/api/view-type';
import { loadExtentFromSession } from 'cadenza/workbook/workbook-session-storage';
import { WORKSHEET_SETTINGS_CONSTANTS } from 'cadenza/workbook/workbook-panel/settings/common-worksheet-settings';
import { ConditionType } from 'cadenza/model/condition-type';
import { WORKBOOK_SETTINGS_CONSTANTS } from 'cadenza/workbook/workbook-panel/settings/workbook-settings-constants';
import { updateColorConfiguration } from 'cadenza/utils/colors/colors';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import { MAP_VIEW_KEYS } from 'cadenza/workbook/workbook-panel/settings/map/map-view-settings';
import { WORKBOOK_ERROR_KEYS } from 'cadenza/workbook/workbook-error-keys';
import { getWorkbook, updateWorkbookUrl } from 'cadenza/workbook/workbook-utils';
import { VIEW_SETTINGS_CONSTANTS } from 'cadenza/workbook/workbook-panel/settings/common-view-settings';
import { getSetting, getSettingOrSettingDefault } from 'cadenza/workbook/workbook-settings-util';
import { cadenzaSessionStorage } from 'cadenza/utils/session-storage/session-storage';
import { hasCapabilities } from 'cadenza/model/data-source';
import { conditionHasValues } from 'cadenza/workbook/conditions/condition-util';
import { ATTRIBUTE_BUNDLE_SETTINGS_CONSTANTS } from 'cadenza/workbook/feature-info-settings-dialog/attribute-bundle-settings-constants';
import { calculateWorksheetViewBorders } from 'cadenza/workbook/worksheet/worksheet-util';
import { ObjecttypeBoundState } from 'cadenza/workbook/api/objecttype';

const logger = getLogger('cadenza/workbook');

export const SLICE_PREFIX = 'workbookSlice'; // exported for testing
const WORKBOOK_CHANGED_KEY = 'workbook-changed';

/**
 * Enum for the change status of the workbook.
 *
 * @enum {string}
 * @readonly
 */
const CHANGE_STATE = {
  NO: 'no',
  MINOR: 'minor',
  MAJOR: 'major'
};

if (cadenzaTopWindow !== window.self) {
  logger.error('Workbook state must not be called from iframe code. Only top-level code allowed.');
}

/**
 * Creates an async thunk for fetching the initial workbook state.
 *
 * @return A command result (reusing the DTO from the command execution)
 * @see WorkbookCommandResultDto.java
 */
export const fetchInitialState = createAsyncThunk(SLICE_PREFIX + '/fetchInitialState', (arg, thunkAPI) => {
  const state = slice(thunkAPI.getState());
  // do not catch the error here: it would result in an infinite loading spinner as the caller relies on the "rejected" state of redux as this is executed async
  return getWorkbookState(state.workbookIdAndHash, state.activeWorksheetId);
});

export const restoreState = createAsyncThunk(SLICE_PREFIX + '/restoreState', (marker) => {
  const { workbookIdAndHash, activeWorksheetId } = marker;
  // do not catch the error here: it would result in an infinite loading spinner as the caller relies on the "rejected" state of redux as this is executed async
  return getWorkbookState(workbookIdAndHash, activeWorksheetId);
});

let lastCommand;
let currentCommandPromise = null;

/**
 * Creates an async thunk for executing a command.
 *
 * Only one command is executed at a time.
 *
 * @param {import('cadenza/api-client/workbook-api').Command} command - The command to be executed
 * @return {AsyncThunk<Promise<object>, void, {}>}
 * @return The command result
 * @see WorkbookCommandResultDto.java
 */
export const executeCommand = createAsyncThunk(SLICE_PREFIX + '/executeCommand', (command, thunkAPI) => {
  logger.log('Schedule command', command);

  /*
   * Promise.prototype.finally() won't work, because the return value of the finally callback
   * is ignored, i.e. Promise.resolve(1).finally(() => 2) will be resolved with 1.
   * See https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Promise/finally
   */
  const state = slice(thunkAPI.getState());
  currentCommandPromise = Promise.allSettled([ currentCommandPromise ])
    .then(() => {
      logger.info('Execute command', command);
      lastCommand = command;
      return executeCommandRequest(state.workbookIdAndHash, command);
    })
    .then(result => {
      logger.log('Command executed', command, result);
      return result;
    })
    .catch(error => {
      logger.error('Failed to execute command', command, error);
      // In order to pass the error to the `rejected` callback, we need to use this Redux method
      // instead of just throwing the error here.
      return thunkAPI.rejectWithValue(error);
    })
    .finally(() => {
      currentCommandPromise = null;
    });
  return currentCommandPromise;
});

const parsedWorkbookUrl = parseWorkbookUrl(location.pathname);

initializePanelVisibility();

function setStateFromUpdate (state, action) {
  const {
    repositoryName,
    repositoryItemName,
    repositoryHasCmdb,
    publishingProcessEnabled,
    operations,
    invalidViewsOrDataViews,
    dataViewsAffectedBySelectionChange,
    activeAttributeIds,
    workbook: { displayState, ...workbook },
    version,
    versionMode
  } = action.payload;
  Object.assign(state,
    updateWorkbookState(state, { patch: operations, activeAttributeIds }),
    { repositoryName, repositoryItemName, repositoryHasCmdb, publishingProcessEnabled, version, versionMode });
  updateColorConfiguration(getColorConfiguration(state));
  const panelVisibility = getPanelVisibilityFromDisplayState(displayState);
  const workbookWasOpenedFromNavigator = Boolean(panelVisibility);
  if (workbookWasOpenedFromNavigator) {
    Object.entries(panelVisibility).forEach(([ panelId, visible ]) => {
      state.panelById[panelId].visible = visible;
    });
    state.clientChanged = false;
  }
  updateLegacyWorkbookState(workbook, { repositoryName, repositoryItemName });
  updateDataViewInvalidationState(state, invalidViewsOrDataViews, dataViewsAffectedBySelectionChange);

  if (action.type === SLICE_PREFIX + '/restoreState/fulfilled') {
    const { workbookIdAndHash, changed, clientChanged } = action.meta.arg;
    state.workbookIdAndHash = workbookIdAndHash;
    state.changed = changed;
    state.clientChanged = clientChanged;
  }
}

let legacyWorkbookState;

const workbookSlice = createSlice({
  name: SLICE_PREFIX,
  initialState: {
    /**
     * `WorkbookIdAndHash` of the current workbook
     *
     * @type {import('cadenza/workbook/api/workbook-id-and-hash').WorkbookIdAndHash}
     */
    workbookIdAndHash: parsedWorkbookUrl?.workbookIdAndHash,
    /**
     * The ID of the active (currently shown) worksheet
     *
     * @type {import('cadenza/workbook/api/worksheet-id').WorksheetId}
     */
    activeWorksheetId: parsedWorkbookUrl?.worksheetId,
    /**
     * The ID of the active view or '' if no view is active
     *
     * @type {import('cadenza/workbook/api/worksheet-id').WorksheetId | ''}
     */
    activeViewId: '',
    /**
     * The ID of the maximized view or '' if no view is maximized
     *
     * @type {import('cadenza/workbook/api/worksheet-id').WorksheetId | ''}
     */
    maximizedViewId: '',
    /**
     * The name of the workbook's repository
     *
     * @type {string}
     */
    repositoryName: '',
    /**
     * The name of the workbook's repository item
     *
     * _Note:_ The tuple `[ repositoryName, repositoryItemName ]` forms the
     * {@link import('cadenza/api-client/repository-item-api').RepositoryItemKey} of the workbook.
     *
     * @type {string}
     */
    repositoryItemName: '',
    /**
     * True if the workbook's repository has a CMDB
     *
     * @type {boolean}
     */
    repositoryHasCmdb: false,
    /**
     * Whether the workbook repository has publishing process enabled
     *
     * @type {boolean}
     */
    publishingProcessEnabled: false,
    /**
     * The print name of the workbook
     *
     * @type {string}
     */
    name: '',
    /**
     * The version of the workbook
     *
     * @type {number | undefined}
     */
    version: undefined,
    /**
     * The version mode of the workbook saved in backend (a subset of VersionMode values)
     *
     * @type  {BackendVersionMode | undefined}
     */
    versionMode: undefined,
    /**
     * Whether the workbook is saved (tri-state!)
     *
     * @type {boolean | undefined}
     */
    saved: undefined,
    /**
     * Whether the workbook is changed, i.e. whether a command was executed since it was last saved.
     *
     * _Note:_ An unsaved workbook is considered changed.
     *
     * @type {'no' | 'minor' | 'major'}
     */
    changed: CHANGE_STATE.MAJOR,
    /**
     * Whether the workbook was changed on the client
     *
     * _Note:_ This is a hack for changes that are not caused by a command execution
     * but should still be persisted on save.
     *
     * @type {boolean}
     */
    clientChanged: booleanFromSessionStorage(WORKBOOK_CHANGED_KEY),
    /**
     * Privileges of the acting user on the workbook
     *
     * @type {import('cadenza/access-management/object-privilege').ObjectPrivilege}
     */
    privileges: [],
    /**
     * Type of the workbook
     *
     */
    workbookType: 'WORKBOOK',
    /**
     * The workbook's settings
     *
     * @type {Object<string, string>}
     */
    settings: {},
    /**
     * The workbook config from `workbook-config.xml`
     *
     * @type {object}
     * @see WorkbookConfigDto.java
     */
    config: {},

    /**
     * The status of the initial workbook state fetching (see `fetchInitialState()`)
     *
     * @type {import('cadenza/state/state-utils').STATE}
     */
    initializationStatus: STATUS.PENDING,
    /**
     * The status of the current command execution (see `executeCommand()`) or 'pending' if no command is being executed
     *
     * @type {import('cadenza/state/state-utils').STATE}
     */
    commandStatus: STATUS.PENDING,
    /**
     * The error key of the previously executed command error '' if there was no error
     *
     * Used to show specific messages for certain errors.
     *
     * @type {string}
     * @see problemType in WorkbookExceptionHandler.java
     */
    commandErrorKey: '',
    /**
     * The error detail of the previously executed command error or null if there was no error
     *
     * @type {unknown | null}
     */
    commandErrorDetail: null,

    /**
     * Datasources of the objecttypes that were added to the workbook
     *
     * @type {import('cadenza/model/data-source').DataSource[]}
     */
    datasources: [],
    /**
     * Objecttypes that were added to the workbook by ID
     *
     * @type {Object<import('cadenza/repository/objecttype-id').ObjecttypeId, import('cadenza/workbook/api/objecttype').Objecttype>}
     */
    objectTypeById: {},
    /**
     * Attributes of the objecttypes that were added to the workbook by ID
     *
     * @type {Object<import('cadenza/workbook/api/attribute-id').AttributeId, import('cadenza/workbook/api').AttributeDto>}
     */
    attributeById: {},
    /**
     * Attributes that are active (used) in one of the workbook's data views by (active!) ID
     *
     * @type {Object<import('cadenza/workbook/api/attribute-id').ActiveAttributeId, import('cadenza/workbook/api').AttributeDto>}
     */
    activeAttributeById: {},

    /**
     * The current dimension/measure value selection in the workbook or undefined if there's no selection
     *
     * @type {WorkbookSelectionDto|undefined}
     */
    selection: undefined,

    /**
     * The IDs of the worksheets of the workbook (in order)
     *
     * @type {import('cadenza/workbook/api/worksheet-id').WorksheetId[]}
     */
    worksheets: [],
    /**
     * The worksheets of the workbook by ID
     *
     * @type {Object<import('cadenza/workbook/api/worksheet-id').WorksheetId, import('cadenza/workbook/api/worksheet-dto').WorksheetDto>}
     */
    worksheetById: {},

    /**
     * The views of the workbook by ID
     *
     * @type {Object<import('cadenza/workbook/api/view-id').ViewId, import('cadenza/workbook/api/view-dto').ViewDto>}
     */
    viewById: {},
    /**
     * Metadata of the views of the workbook by ID
     *
     * _Note:_ These client-side metadata are currently separate from the actual views
     * so that they survive view updates from the server.
     *
     * @type {Object<import('cadenza/workbook/api/view-id').ViewId, { loadingState: import('cadenza/workbook/view-loading-state/view-loading-state').LOADING_STATES }>}
     */
    viewMetadataById: {},
    /**
     * IDs of views and data views that need to be refreshed
     *
     * @type {(import('cadenza/workbook/api/view-id').ViewId | import('cadenza/workbook/api/data-view-id').DataViewId)[]}
     */
    invalidViewsOrDataViews: [],
    /**
     * IDs of data views that have an outdated selection state
     *
     * @type {import('cadenza/workbook/api/data-view-id').DataViewId[]}
     */
    dataViewsAffectedBySelectionChange: [],

    /**
     * The data views of the workbook by ID
     *
     * @type {Object<import('cadenza/workbook/api/data-view-id').DataViewId, import('cadenza/workbook/api/data-view-dto').DataViewDto>}
     */
    dataViewById: {},

    /**
     * Workbook specific drill-through definitions
     * (not including the legacy drill-throughs defined via Cadenza Desktop)
     *
     * @type {import('cadenza/workbook/drill-though/dto/drill-through-dto').DrillThroughDto[]}
     */
    drillThroughTargets: [],

    /**
     * List of condition IDs and condition group IDs that defines the order within the global analysis context
     *
     * @type {(import('cadenza/api-client/workbook-api').ConditionId | import('cadenza/workbook/model/condition-dto-group').ConditionGroupId)[]}
     */
    conditionsAndGroups: [],
    /**
     * All conditions of the workbook by ID (global and locals ones)
     *
     * @type {Object<import('cadenza/workbook/model/condition-dto').ConditionId, import('cadenza/workbook/model/condition-dto').ConditionDto>}
     */
    conditionById: {},
    /**
     * Views and data views affected by a condition by condition ID
     *
     * @type {Object<import('cadenza/workbook/model/condition-dto').ConditionId, object>}
     * @see ConditionToViewsRelationsDto.java
     */
    affectedViewsByConditionId: {},
    /**
     * Conditions groups of the workbook by ID (Always global, we don't have local condition groups.)
     *
     * @type {Object<import('cadenza/workbook/model/condition-dto-group').ConditionGroupId, import('cadenza/workbook/model/condition-dto-group').ConditionGroupDto>}
     */
    conditionGroupById: {},

    /**
     * The workbook panel states by panel ID
     *
     * Panels are visible and not disabled by default.
     *
     * @type {Object<import('cadenza/workbook/workbook-panel/workbook-panel-utils').WorkbookPanelName, { visible: boolean, disabled: boolean }>}
     */
    panelById: Object.fromEntries(
      Object.values(WorkbookPanelName).map(panel => [ panel, {
        visible: booleanFromSessionStorage(panel, true),
        disabled: false
      } ])
    )
  },
  reducers: {
    // Should be a thunk, but too complicated for now
    updateWorkbookMetadata (state, {
      payload: {
        workbookIdAndHash = state.workbookIdAndHash,
        repositoryName = state.repositoryName,
        repositoryItemName = state.repositoryItemName,
        publishingProcessEnabled = state.publishingProcessEnabled,
        name = state.name,
        version = state.version,
        versionMode = state.versionMode,
        saved = state.saved,
        changed = state.changed,
        clientChanged = state.clientChanged,
        privileges = state.privileges,
        workbookType = state.workbookType
      }
    }) {
      if (saved && state.saved === false) {
        changed = CHANGE_STATE.NO;
        clientChanged = false;
      }
      Object.assign(state, {
        workbookIdAndHash,
        repositoryName,
        repositoryItemName,
        publishingProcessEnabled,
        name,
        saved,
        changed,
        clientChanged,
        version,
        versionMode,
        privileges,
        workbookType
      });
    },
    updateViewMetadata: {
      prepare: (viewId, metadata) => ({ payload: { viewId, metadata } }),
      reducer: (state, { payload: { viewId, metadata } }) => {
        let viewMetadata = state.viewMetadataById[viewId];
        if (!viewMetadata) {
          viewMetadata = {};
          state.viewMetadataById[viewId] = viewMetadata;
        }
        Object.entries(metadata).forEach(([ key, value ]) => {
          if (value !== undefined) {
            viewMetadata[key] = value;
          } else {
            delete viewMetadata[key];
          }
        });
      }
    },
    resetViewMetadata (state, viewId) {
      delete state.viewMetadataById[viewId];
    },
    removeObjectTypes (state, { payload }) {
      Object.assign(state, removeObjecttypesById(state, payload));
    },
    togglePanelVisibility: {
      prepare: (panelId, visible) => ({ payload: { panelId, visible } }),
      reducer: (state, { payload: { panelId, visible = !state.panelById[panelId].visible } }) => {
        const previousVisibility = state.panelById[panelId].visible;
        state.panelById[panelId].visible = Boolean(visible);
        state.clientChanged = (state.panelById[panelId].visible !== previousVisibility);
      }
    },
    hideAllPanels (state) {
      Object.values(state.panelById).forEach(panel => { panel.visible = false; });
    },
    activateView (state, { payload: viewId }) {
      activateViewInternally(state, viewId);
    },
    maximizeView (state, { payload: viewId }) {
      state.maximizedViewId = viewId;
    },
    forceMaximizeView (state, { payload: viewId }) {
      if (viewId) {
        state.forceMaximizePreviousState = {
          maximizedViewId: state.maximizedViewId,
          panelById: cloneDeep(state.panelById)
        };

        state.maximizedViewId = viewId;
        Object.values(WorkbookPanelName).forEach(panelId => {
          state.panelById[panelId] = { visible: false, disabled: true };
        });
      } else {
        Object.assign(state, state.forceMaximizePreviousState);
        delete state.forceMaximizePreviousState;
      }
    },
    initializeWorksheet (state) {
      activateOnlyViewIfAvailable(state);
    },
    unmarkViewAsDirty (state, { payload: viewId }) {
      const view = getViewById(state, viewId);
      const dataViewIds = view.dataViews;

      state.invalidViewsOrDataViews = state.invalidViewsOrDataViews
        .filter(viewIdOrDataViewId => view.id !== viewIdOrDataViewId
          && !dataViewIds.includes(viewIdOrDataViewId));
      state.dataViewsAffectedBySelectionChange = state.dataViewsAffectedBySelectionChange
        .filter(dataViewId => !dataViewIds.includes(dataViewId));
    },
    invalidateDataViewsUsingTheObjecttype (state, { payload: objecttypeId }) {
      const dataViewIdsUsingObjecttype = getDataViewIdsUsingObjecttype(state, objecttypeId);

      for (const dataViewId of dataViewIdsUsingObjecttype) {
        if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
          state.invalidViewsOrDataViews.push(dataViewId);
        }
      }
    },
    updateObjecttypeBoundState (state, { payload: { objecttypeId, boundState } }) {
      const objecttype = slice(state).objectTypeById[objecttypeId];
      objecttype.boundState = boundState;
    },
    updateObjecttypeBoundStateOnSave (state) {
      for (let objecttypeId in slice(state).objectTypeById) {
        const objecttype = slice(state).objectTypeById[objecttypeId];
        if (objecttype.boundState === ObjecttypeBoundState.TRANSIENT) {
          objecttype.boundState = ObjecttypeBoundState.BOUND_TO_WORKBOOK;
        }
      }
    },
    updateWorkbookName (state, { payload }) {
      state.name = payload;
    }
  },
  extraReducers: {
    // The "pending", "fulfilled" and "rejected" actions are dispatched automatically.
    // Hence, the reducers go in a separate structure.
    [fetchInitialState.pending]: (state) => {
      state.initializationStatus = STATUS.PENDING;
    },
    [fetchInitialState.fulfilled]: (state, action) => {
      state.initializationStatus = STATUS.SUCCEEDED;
      setStateFromUpdate(state, action);
    },
    [fetchInitialState.rejected]: (state) => {
      state.initializationStatus = STATUS.FAILED;
    },
    [restoreState.fulfilled]: (state, action) => {
      setStateFromUpdate(state, action);
    },
    [restoreState.rejected]: () => {
      logger.error('Could not restore redux state!');
    },
    [executeCommand.pending]: (state) => {
      state.commandStatus = STATUS.PENDING;
    },
    [executeCommand.fulfilled]: (state, action) => {
      state.commandStatus = STATUS.SUCCEEDED;
      if (state.changed !== CHANGE_STATE.MAJOR) {
        state.changed = action.meta.arg.majorChange
          ? CHANGE_STATE.MAJOR
          : CHANGE_STATE.MINOR;
      }
      const {
        workbookIdAndHash,
        workbook,
        operations,
        invalidViewsOrDataViews,
        dataViewsAffectedBySelectionChange,
        activeAttributeIds
      } = action.payload;
      Object.assign(state, updateWorkbookState(state, {
        patch: operations,
        activeAttributeIds
      }), { workbookIdAndHash });
      legacyWorkbookState = workbook;
      updateLegacyWorkbookState(workbook, { command: lastCommand, hash: workbookIdAndHash.hash });
      updateDataViewInvalidationState(state, invalidViewsOrDataViews, dataViewsAffectedBySelectionChange);

      const viewIds = new Set(Object.keys(state.viewById));
      state.viewMetadataById = Object.fromEntries(
        Object.entries(state.viewMetadataById).filter(([ viewId ]) => viewIds.has(viewId)));

      state.commandErrorKey = '';
      state.commandErrorDetail = null;
    },
    [executeCommand.rejected]: (state, action) => {
      // if the actual error is swallowed (our default) then the `payload` is `undefined`
      state.commandErrorKey = action.payload?.key || '';
      state.commandErrorDetail = createCommandErrorDetails(action.payload);
      state.commandStatus = STATUS.FAILED;
    }
  }
});

/**
 * Updates the given workbook state with a patch (command response) from the server.
 *
 * @param {object} state - The state object to update
 * @param {object} context
 * @param {object} context.patch - A JSON patch
 * @param {string[]} context.activeAttributeIds - list of attribute ids which mark attributes currently used
 * @return {object} The updated state object
 */
function updateWorkbookState (state, { patch, activeAttributeIds }) {
  const nextState = applyPatch(state, patch).newDocument;
  const activeAttributeIdSet = new Set(activeAttributeIds);
  // modifying attribute.active directly will result in an "unable to modify immutable object" error
  // when adding a new slice when the redux devtools extension is open. The modification should be a
  // valid operation since the next state is the proxied state, but nevertheless it results in an
  // error. Recalculating the attributeById property does not result in this error.
  // We should continue to investigate this to find out why this is problematic.
  nextState.attributeById = Object.entries(nextState.attributeById).reduce(
    (obj, [ attributeId, attribute ]) => {
      obj[attributeId] = { ...attribute, active: activeAttributeIdSet.has(attributeId) };
      return obj;
    }, {});
  const activeWorksheet = getActiveWorksheet(state);
  if (!activeWorksheet || isOnDemand(activeWorksheet.settings)
    && shouldOnDemandBeConsidered(state)
    && hasDirtyViews(nextState)) {
    state.activeViewId = '';
  } else {
    activateOnlyViewIfAvailable(state);
  }
  return nextState;
}

function hasDirtyViews (nextState) {
  return nextState.invalidViewsOrDataViews.length > 0;
}

/**
 * Activates the only view of the active worksheet if the designer is visible.
 *
 * @param {object} state - The workbook state slice
 */
function activateOnlyViewIfAvailable (state) {
  const views = getActiveWorksheetViews(state);
  if (state.panelById[WorkbookPanelName.VIEW_SETTINGS].visible && views.length === 1) {
    state.activeViewId = views[0].id;
  }
}

function updateLegacyWorkbookState (workbook, options) {
  // Must be async, to that views do not subscribe to the store while the reducer still executes.
  setTimeout(() => getWorkbook().updateWorkbookState(workbook, options));
}

function removeObjecttypesById ({
  objectTypeById: objecttypeById,
  attributeById,
  datasources: dataSources
}, objecttypeIdsToRemove) {
  assert(Array.isArray(objecttypeIdsToRemove), 'objecttypeIdsToRemove has to be an array');
  // remove object types and their attributes
  for (const objecttypeId of objecttypeIdsToRemove) {
    if (!objecttypeById[objecttypeId]) {
      continue;
    }
    const attributeIds = objecttypeById[objecttypeId].attributes;
    for (const attributeId of attributeIds) {
      delete attributeById[attributeId];
    }
    delete objecttypeById[objecttypeId];
  }

  // remove obsolete datasources
  const uniqueDataSourceIds = new Set(
    Object.values(objecttypeById).map(({ dataSourceId }) => dataSourceId)
  );
  const newDataSources = dataSources.filter(({ name }) => uniqueDataSourceIds.has(name));

  return { objectTypeById: objecttypeById, attributeById, datasources: newDataSources };
}

const { reducer, actions } = workbookSlice;
registerReducer(SLICE_PREFIX, reducer);

// Action creators

export const {
  updateWorkbookMetadata,
  updateViewMetadata,
  resetViewMetadata,
  removeObjectTypes,
  togglePanelVisibility,
  hideAllPanels,
  activateView,
  maximizeView,
  forceMaximizeView,
  initializeWorksheet,
  unmarkViewAsDirty,
  invalidateDataViewsUsingTheObjecttype,
  updateObjecttypeBoundState,
  updateObjecttypeBoundStateOnSave,
  updateWorkbookName
} = actions;

export const showPanel = (panelId) => togglePanelVisibility(panelId, true);
export const hidePanel = (panelId) => togglePanelVisibility(panelId, false);

export const deactivateView = () => activateView('');
export const minimizeView = () => maximizeView('');

// Selectors

// eslint-disable-next-line jsdoc/require-description,jsdoc/require-returns
/** @deprecated */
export const getAndResetLegacyWorkbookState = () => {
  const result = legacyWorkbookState;
  legacyWorkbookState = null; // free memory
  return result;
};

export const isWorkbookActive = (state) => !!slice(state).workbookIdAndHash;

export const getInitializationStatus = (state) => slice(state).initializationStatus;
export const getCommandStatus = (state) => slice(state).commandStatus;

export const getWorkbookIdAndHash = (state) => slice(state).workbookIdAndHash;
export const getWorkbookVersion = (state) => slice(state).version;
/**
 * Returns workbook version mode.
 * It may be a mode coming from the backend (if the repository has publishing process enabled and there version is marked as 'published' or 'development')
 * or calculated here.
 *
 * @param state
 * @return {VersionMode|string}
 */
export const getWorkbookVersionMode = (state) => {
  const publishingProcessEnabled = isRepositoryPublishingProcessEnabled(state);
  const saved = isWorkbookSaved(state);
  if (!publishingProcessEnabled) {
    return saved ? 'saved' : 'unsaved';
  }
  // set by the backend?
  const savedVersionMode = slice(state).versionMode;
  if (savedVersionMode) {
    return savedVersionMode;
  }
  // a saved workbook with publishingProcess is a draft
  return saved ? 'development' : 'unsaved';
};
export const getActiveWorksheetId = (state) => slice(state).activeWorksheetId;
export const getWorksheetById = (state, worksheetId) => slice(state).worksheetById[worksheetId];

export const isRepositoryPublishingProcessEnabled = (state) => slice(state).publishingProcessEnabled;
/**
 * Returns all worksheets of the workbook.
 *
 * @param {object} state - The state object
 * @return {import('cadenza/workbook/api/worksheet-dto').WorksheetDto[]} All worksheets of the workbook
 */
export const getWorksheets = (state) => {
  const worksheetIds = slice(state).worksheets;
  return worksheetIds.map((id) => getWorksheetById(state, id));
};

export const getFirstWorksheet = (state) => getWorksheets(state)[0];
export const getActiveWorksheet = (state) => getWorksheetById(state, getActiveWorksheetId(state));
export const isActiveWorksheetOnDemand = (state) => !getActiveWorksheet(state) || isOnDemand(getActiveWorksheet(state).settings) && shouldOnDemandBeConsidered(state);
export const getWorksheetByViewId = (state, viewId) => getWorksheets(state)
  .find(worksheet => worksheet.views.includes(viewId));
export const getWorkbookRepositoryName = (state) => slice(state).repositoryName;
export const workbookRepositoryHasCmdb = (state) => slice(state).repositoryHasCmdb;
export const getWorkbookKey = (state) => {
  const { repositoryName, repositoryItemName } = slice(state);
  return (repositoryName && repositoryItemName) ? {
    repositoryName,
    id: repositoryItemName
  } : undefined;
};
export const getWorkbookPrivileges = (state) => slice(state).privileges;
export const getWorkbookName = (state) => slice(state).name;
export const getWorkbookType = (state) => slice(state).workbookType;
export const getWorkbookRelation = (state) => slice(state).relation;
export const getWorkbookSettingsPrintHeader = (state) => slice(state).settings[WORKBOOK_SETTINGS_CONSTANTS.WORKBOOK_PRINT_HEADER_NAME];
export const isWorkbookSaved = (state) => slice(state).saved;
export const isChanged = (state) => slice(state).changed;
export const isClientChanged = (state) => slice(state).clientChanged;
export const isWorkbookChanged = (state) => slice(state).changed === CHANGE_STATE.MINOR || slice(state).changed === CHANGE_STATE.MAJOR || slice(state).clientChanged;
export const hasMajorChange = (state) => slice(state).changed === CHANGE_STATE.MAJOR;
export const isWorkbookSharable = (state) => hasPrivilege(slice(state).privileges, ObjectPrivilege.share);
export const isWorkbookWritable = (state) => hasPrivilege(slice(state).privileges, ObjectPrivilege.write);
export const isWorkbookVisibilityRestricted = (state) => Boolean(slice(state).saved) && isVisibilityRestricted(slice(state).privileges);
export const getPrintHeaders = (state) => slice(state).config.printHeaders;
export const getColorConfiguration = (state) => slice(state).config.colorConfiguration;
export const getAttributeDescriptionVisibility = (state) => slice(state).config.attributeDescriptionVisibility;

/**
 * Checks if the workbook is empty.
 * _Note_: Here, empty refers to having no views, no data sources, no object types and only one worksheet.
 * Workbook settings like drillthrough targets, the worksheet names etc. are not taken into account here.
 *
 * @param {object} state - The state object
 * @return {boolean} Whether the workbook is empty or not
 */
export const isWorkbookEmpty = (state) =>
  (getViewIds(state).length === 0)
  && (getDataSources(state).length === 0)
  && (getObjecttypes(state).length === 0)
  && (getWorksheets(state).length === 1);

/**
 * Returns the workbook specific drill-through targets (not including the legacy targets
 * defined via Cadenza Desktop)
 *
 * @param {object} state - The state object
 * @return {import('cadenza/workbook/drill-though/dto/drill-through-dto').DrillThroughDto[]} All targets defined
 *   in this workbook
 */
export const getDrillThroughTargets = (state) => slice(state).drillThroughTargets;

/**
 * Returns the active worksheets background color if it is set, otherwise the first set background
 * color of any previous worksheet ("auto"-setting). The default is the background color set in theming.
 * If worksheetId is passed, returns the background color of the worksheet with worksheetId.
 *
 * @param {object} state - The state object.
 * @param {string} [worksheetId] - Optional: return background color of worksheet with worksheetId.
 * @return {import('cadenza/utils/colors/colors').Color} - The calculated background color of the worksheet.
 */
export const getWorksheetBackgroundColor = (state, worksheetId) => {
  const worksheets = getWorksheets(state);
  const worksheet = worksheetId
    ? worksheets.find((ws) => ws.id === worksheetId)
    : getActiveWorksheet(state);
  if (worksheet) {
    const getWsBackgroundColor = (ws) => { // Get worksheet background color without "auto"
      return ws != null
        ? ws.settings[WORKSHEET_SETTINGS_CONSTANTS.WORKSHEET_BACKGROUND_COLOR]
        : null;
    };

    let color = getWsBackgroundColor(worksheet);
    if (color !== null && color !== undefined) {
      return color;
    }

    const worksheetIndex = worksheets.map(ws => ws.id).indexOf(worksheet.id);
    const previousWorksheets = worksheets.slice(0, worksheetIndex);
    while (previousWorksheets.length > 0) {
      color = getWsBackgroundColor(previousWorksheets.pop());
      if (color !== null && color !== undefined) {
        return color;
      }
    }
  }
  return window.Disy.theme['worksheet-bg'] ?? '#FFFFFF';
};

export const getWorksheetViewBorders = (state, worksheetId) => {
  return calculateWorksheetViewBorders(worksheetId ?? getActiveWorksheetId(state), state);
};

export const getAllAttributes = (state) => Object.values(slice(state).attributeById);
export const getAttributes = createSelector(getAllAttributes, attributes => attributes.filter(attribute => attribute.visible));
export const getAttributeById = (state, attributeId) => {
  return slice(state).attributeById[attributeId] || slice(state).activeAttributeById[attributeId];
};
export const getBaseAttributeId = (state, attributeId) => {
  const attribute = getAttributeById(state, attributeId);
  return attribute.baseId ?? attribute.id;
};

export const getReachableObjecttypeIds = (state, objecttypeId, onlyOneToOneAssociations = false) => {
  const objectType = slice(state).objectTypeById[objecttypeId];
  // unknown object types would crash the ui
  if (objectType) {
    const objecttypeIds = onlyOneToOneAssociations
      ? objectType.reachableObjecttypesWithOneToOneAssociation
      : objectType.reachableObjecttypes;
    return objecttypeIds ? [ ...objecttypeIds ] : [];
  }
  logger.error(`No objectType found by id "${objecttypeId}"`);
  return [];
};

/**
 * Return all attributes which are reachable by the main OT of the specified workbook's views.
 *
 * @param {object} state - The state object
 * @param {import('cadenza/workbook/api/worksheet-id').WorksheetId} worksheetId - ID of worksheet to
 *   return attributes of
 * @return {import('cadenza/workbook/api/attribute-id').AttributeId[]} All reachable attributes
 */
export const getReachableAttributesByWorksheetId = (state, worksheetId) => {
  return uniq( // Since every worksheet could list the same attributes (eg. when copy) we deduplicate
    slice(state).worksheetById[worksheetId].views
      .flatMap(viewId => getReachableAttributesByViewId(state, viewId)));
};

/**
 * Return all attributes which are reachable by the main OT of the specified view.
 *
 * @param {object} state - The state object
 * @param {import('cadenza/workbook/api/view-id').ViewId} viewId - ID of view to return reachable
 *   attributes for
 * @return {import('cadenza/workbook/api/attribute-id').AttributeId[]} All reachable attributes
 */
export const getReachableAttributesByViewId = (state, viewId) => {
  const currentSlice = slice(state);

  const viewById = currentSlice.viewById;
  const dataViewById = currentSlice.dataViewById;
  const objecttypeById = currentSlice.objectTypeById;

  return uniq(  // Since every dataView could list the same attributes (eg. when copy) we deduplicate
    viewById[viewId].dataViews
      .flatMap(dataViewId => dataViewById[dataViewId])
      .flatMap(dataView => {
        // When removing the Objecttype from the workbook, the reference to it is kept in the data
        // view (which then fails validation and the user is informed). Such a dangling reference
        // would create a NPE here. Returning [] in such cases allows to compile the desired list.
        return objecttypeById[dataView.mainObjecttype.id]?.reachableObjecttypes || [];
      })
      .flatMap(objecttypeId => objecttypeById[objecttypeId].attributes));
};

// This selector returns all object types, including ones with restricted visibility.
// In most cases only visible unrestricted object types are required, so use getVisibleObjectTypes or getVisibleObjecttypeIds instead.
const getObjecttypes = createSelector((state) => slice(state).objectTypeById, Object.values);

export const getObjecttypeById = (state, objecttypeId) => {
  return slice(state).objectTypeById[objecttypeId];
};
export const getAllAttributesFromObjectType = createSelector(
  [ getObjecttypeById, (state) => slice(state).attributeById ],
  (objectType, attributesById) => objectType.attributes
    .map(attributeId => attributesById[attributeId]));

export const getVisibleObjecttypes = createSelector(getObjecttypes, objecttypes => objecttypes.filter(objectType => !isVisibilityRestricted(objectType.privileges)));
export const getVisibleObjecttypeIds = createSelector(getVisibleObjecttypes, visibleObjecttypes => visibleObjecttypes.map(({ id }) => id));
export const getObjecttypesWithAttributes = createSelector(getObjecttypes, resolveObjecttypeAttributes);

function isVisibilityRestricted (privileges) {
  return privileges !== null && !hasPrivilege(privileges, ObjectPrivilege.visible);
}

const getObjecttypesByAttributeId = createSelector(getObjecttypes,
  (objectTypes) => Object.fromEntries(objectTypes
    .flatMap((objectType) => objectType.attributes
      .map((attributeId) => [ attributeId, objectType ]))));

export const getObjecttypeByAttributeId = (state, attributeId) => {
  return getObjecttypesByAttributeId(state)[attributeId];
};
export const hasObjecttypeWithPrintName = (state, name) => {
  return getObjecttypes(state).some(ot => ot.printName === name);
};

export const getDataSources = (state) => slice(state).datasources;

export const getVisibleDataSources = createSelector(getDataSources, getVisibleObjecttypes, filterEmptyDatasources);

function filterEmptyDatasources (datasources, visibleObjecttypes) {
  return datasources.filter(({ name }) => visibleObjecttypes.some(({ dataSourceId }) => dataSourceId === name));
}

function getDataSourceById (state, dataSourceId) {
  return getDataSources(state).find(datasource => datasource.name === dataSourceId);
}

export function getDataSourceByObjecttypeId (state, objecttypeId) {
  return getDataSourceById(state, getObjecttypeById(state, objecttypeId)?.dataSourceId);
}

export function hasObjecttypeDatasourceCapabilities (state, objecttypeId, capabilities) {
  const dataSource = getDataSourceByObjecttypeId(state, objecttypeId);
  return hasCapabilities(dataSource, capabilities);
}

export const getConditionsAndGroups = (state) => slice(state).conditionsAndGroups;
export const getConditionById = (state, conditionId) => slice(state).conditionById[conditionId];
export const getConditionsById = (state) => slice(state).conditionById;
export const getConditionGroupById = (state, conditionId) => slice(state).conditionGroupById[conditionId];
export const getConditionGroupsById = (state) => slice(state).conditionGroupById;

export const isGlobalCondition = (state, conditionId) => [
  ...slice(state).conditionsAndGroups,
  ...Object.values(slice(state).conditionGroupById).flatMap(group => group.conditions)
].includes(conditionId);

export const getConditionsHavingValue = createSelector(getConditionsById, conditionById => {
  const conditionHasValues = condition => condition.values?.valueItems?.length > 0;
  return Object.values(conditionById)
    .filter(conditionHasValues);
});

export const hasSpatialConditions = createSelector(
  getConditionsById,
  conditionById => {
    const conditions = Object.values(conditionById);
    return conditions.some(({ type }) => type === ConditionType.SPATIAL_CONDITION);
  });

export const isFilteredByCondition = (state, dataViewId, conditionId) => {
  return slice(state)
    .affectedViewsByConditionId[conditionId]
    .affectedDataViews?.[getActiveWorksheetId(state)]
    ?.includes(dataViewId);
};

export const getViews = createSelector((state) => slice(state).viewById, Object.values);
export const getViewIds = createSelector((state) => slice(state).viewById, Object.keys);
export const getActiveWorksheetViews = (state) => getActiveWorksheet(slice(state)) ? getActiveWorksheet(slice(state)).views.map(viewId => getViewById(slice(state), viewId)) : [];

export const getViewById = (state, viewId) => addViewMetadata(state, slice(state).viewById[viewId]);
export const getViewByDataViewId = (state, dataViewId) => addViewMetadata(state, getActiveWorksheetViews(state).find(view => view.dataViews.includes(dataViewId)));

function addViewMetadata (state, view) {
  if (view) {
    return { ...view, metadata: slice(state).viewMetadataById[view.id] ?? {} };
  }
}

export const getDataViewById = (state, dataViewId) => slice(state).dataViewById[dataViewId];
const getDataViewIdsUsingObjecttype = (state, objecttypeId) => {
  const dataViewIds = [];

  for (const [ dataViewId, dataView ] of Object.entries(slice(state).dataViewById)) {
    if (Object.values(dataView.attributes) // Only bundle attributes
      .flat() // Unwrap bundles attributes
      .some(attributeId => getAttributeById(state, attributeId).objectTypeId === objecttypeId)) {
      dataViewIds.push(dataViewId);
    }
  }

  return dataViewIds;
};

export const getDataViewIdsByViewId = (state, viewId) => getViewById(state, viewId).dataViews;

export const getViewsAffectedByCondition = (state, conditionId) => {
  const workbookState = slice(state);
  const affectedViewIds = workbookState.affectedViewsByConditionId[conditionId]
    ?.affectedViews
    ?.[getActiveWorksheetId(state)] || [];
  return affectedViewIds.map(viewId => workbookState.viewById[viewId]);
};

export const getViewsThatOverrideConditionLocally = (state, conditionId) => {
  const workbookState = slice(state);
  const condition = workbookState.conditionById[conditionId];
  assert(condition, `No condition with ID=${conditionId} present in the state.`);

  // Spatial conditions actually can't be overridden locally, however they are still present at the
  // view level because they can apply to a different attribute for each view.
  if (condition.type === ConditionType.SPATIAL_CONDITION) {
    return [];
  }

  const affectedViewIds = workbookState.affectedViewsByConditionId[conditionId]
    ?.affectedViewsWithLocalConditions
    ?.[getActiveWorksheetId(state)] || [];
  return affectedViewIds.map(viewId => workbookState.viewById[viewId]);
};

export const getConditionsByViewId = createSelector(
  getActiveWorksheetId,
  getConditionsById,
  (state) => slice(state).viewById,
  (state) => slice(state).affectedViewsByConditionId,
  (
    activeWorksheetId,
    conditionsById,
    viewById,
    affectedViewsByConditionId
  ) => {
    const conditionsByViewId = {};

    Object.values(conditionsById).forEach(condition => {
      const viewsAffectedByCondition = affectedViewsByConditionId[condition.id]
        ?.affectedViews
        ?.[activeWorksheetId]
        ?.map(viewId => viewById[viewId]) || [];

      viewsAffectedByCondition.forEach(view => {
        if (!conditionsByViewId[view.id]) {
          conditionsByViewId[view.id] = [];
        }
        if (!conditionsByViewId[view.id].includes(condition)) {
          conditionsByViewId[view.id].push(condition);
        }
      });
    });

    return conditionsByViewId;
  }
);

export const hasConditionsWithMissingMandatoryValue = createSelector(
  getConditionsById,
  conditionsById => {
    for (const condition of Object.values(conditionsById)) {
      if (condition.settings.mandatory && !conditionHasValues(condition)) {
        return true;
      }
    }
    return false;
  });

export const getSpatialConditionsForAllViews = createSelector(
  getConditionsByViewId,
  (conditionsByViewId) => {
    const spatialConditionsByViewId = {};
    Object.entries(conditionsByViewId)
      .forEach(([ viewId, conditions ]) => {
        const spatialConditions = conditions.filter(condition => condition.type === ConditionType.SPATIAL_CONDITION);
        if (spatialConditions.length) {
          spatialConditionsByViewId[viewId] = spatialConditions;
        }
      });
    return spatialConditionsByViewId;
  }
);

export const getEffectiveSpatialConditionsForAllViews = createSelector(
  getSpatialConditionsForAllViews,
  (spatialConditionsByViewId) => {
    const effectiveSpatialConditionsByViewId = {};
    Object.entries(spatialConditionsByViewId)
      .forEach(([ viewId, conditions ]) => {
        const effectiveSpatialConditions = conditions.filter(isSpatialConditionEffective);
        if (effectiveSpatialConditions.length) {
          effectiveSpatialConditionsByViewId[viewId] = effectiveSpatialConditions;
        }
      });

    return effectiveSpatialConditionsByViewId;
  }
);

function isSpatialConditionEffective (spatialCondition) {
  // it should have a value with at least one polygon
  return Boolean(spatialCondition?.values?.valueItems[0]?.values?.length);
}

export const getSpatialConditionsForView = (state, viewId) => getSpatialConditionsForAllViews(state)[viewId] ?? [];

export const getEffectiveSpatialConditionsForView = (state, viewId) => getEffectiveSpatialConditionsForAllViews(state)[viewId] ?? [];

export const viewHasEffectiveSpatialFilter = (state, viewId) => {
  return getSpatialConditionsForView(state, viewId).length > 0;
};

export const isSpatialConditionGeometryVisibleInView = (state, viewId) => slice(state).viewById[viewId].settings.displaySpatialFilterGeometry;

export const getTimeline3dEnabledView = (state) => Object.values(slice(state).viewById).find(view => view.settings[WORKBOOK_SETTINGS_CONSTANTS.TIMELINE_3D_ENABLED]);

export const isPanelVisible = (state, panelId) => slice(state).panelById[panelId].visible;
export const isPanelDisabled = (state, panelId) => slice(state).panelById[panelId].disabled;

export const getActiveViewId = (state) => slice(state).activeViewId;
export const isActiveView = (state, viewId) => (getActiveViewId(state) === viewId);
export const isViewDirty = (state, viewId) => {
  const invalidViewsOrDataViews = getInvalidViewsOrDataViews(state);
  const dataViewsAffectedBySelectionChange = getDataViewsAffectedBySelectionChange(state);

  if (invalidViewsOrDataViews.includes(viewId)) {
    return true;
  }
  return getViewById(state, viewId).dataViews
    .some(dataViewId => {
      return invalidViewsOrDataViews.includes(dataViewId)
        || dataViewsAffectedBySelectionChange.includes(dataViewId);
    });
};
export const isActiveWorksheetDirty = (state) => slice(state).invalidViewsOrDataViews.length > 0
  || slice(state).dataViewsAffectedBySelectionChange.length > 0;
export const getInvalidViewsOrDataViews = (state) => slice(state).invalidViewsOrDataViews;
export const getDataViewsAffectedBySelectionChange = (state) => slice(state).dataViewsAffectedBySelectionChange;
export const isAnyViewLoading = (state) =>
  Object.values(slice(state).viewMetadataById)
    .some((viewMetadata) => viewMetadata.loadingState === LOADING_STATES.LOADING
      || viewMetadata.loadingState === LOADING_STATES.LOADING_LONG);

export const getMaximizedViewId = (state) => slice(state).maximizedViewId;
export const hasForceMaximizedView = (state) => slice(state).forceMaximizePreviousState != null;
export const getSelection = (state) => slice(state).selection;
export const getDataViewIdsWithAllowedSelection = (state) => Object.keys(slice(state).dataViewById)
  .filter(dataViewId => getSettingOrSettingDefault(
    getDataViewById(state, dataViewId),
    DATA_VIEW_KEYS.COMMON_SELECTION_ENABLED));

export const isViewOnDemand = (state, viewId) => {
  const view = getViewById(state, viewId);
  const isAnyDataViewOnDemand = view.dataViews.some(dataViewId => {
    const dataView = getDataViewById(state, dataViewId);
    return isOnDemand(dataView.settings);
  });
  return isAnyDataViewOnDemand && shouldOnDemandBeConsidered(state);
};

export const getViewDescriptionInView = createSelector(
  getViewById,
  view => getSettingOrSettingDefault(view.settings, VIEW_SETTINGS_CONSTANTS.DESCRIPTION));

export const isFeatureInfoEnabled = (state, dataViewId, bundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, ATTRIBUTE_BUNDLE_SETTINGS_CONSTANTS.FEATURE_INFO_ENABLED, 'true') === 'true';
};

export const isFeatureInfoAutoModeEnabled = (state, dataViewId, bundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, ATTRIBUTE_BUNDLE_SETTINGS_CONSTANTS.FEATURE_INFO_AUTO_MODE_ENABLED, 'true') === 'true';
};

export const getObjectInfoAttributesFromBundle = (state, dataViewId, bundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  return dataView.attributes[bundleName] || [];
};

export const getObjectInfoHeadlineType = (state, dataViewId, bundleName) => {
  if (isFeatureInfoAutoModeEnabled(state, dataViewId, bundleName)) {
    return 'auto';
  }
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, ATTRIBUTE_BUNDLE_SETTINGS_CONSTANTS.FEATURE_INFO_HEADLINE_TYPE, 'auto');
};

export const getObjectInfoCustomHeadline = (state, dataViewId, bundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, ATTRIBUTE_BUNDLE_SETTINGS_CONSTANTS.FEATURE_INFO_HEADLINE_CUSTOM, undefined);
};

export const getIsPieChartSliceValueResolvingEnabled = (state, dataViewId, bundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, ATTRIBUTE_BUNDLE_SETTINGS_CONSTANTS.FEATURE_INFO_PIE_CHART_SLICE_RESOLVING_ENABLED, 'true') === 'true';
};

export const getObjectInfoAttributeBundleName = (state, dataViewId, objecttypeId) => {
  const dataView = getDataViewById(state, dataViewId);
  const objecttypeIdToBundleName = dataView.objecttypeIdToAttributeBundleName ?? {};
  return objecttypeIdToBundleName[objecttypeId];
};

export const selectAttributeBundleSettings = (state, dataViewId) => {
  const dataView = getDataViewById(state, dataViewId);
  return dataView.attributeBundleSettings;
};

/**
 * Clone view to a different worksheet
 *
 * Note: This is just a helper function. Since it's not a reducer it mustn't modify the state. Like
 * regular code, it calls selectors and dispatches a thunk.
 *
 * @param {object} store - Redux store
 * @param {import('cadenza/workbook/api/view-id').ViewId} viewId - ID of source view.
 * @param {import('cadenza/workbook/api/worksheet-id').WorksheetId} targetWorksheetId - ID of target worksheet
 * @return {Promise} - executeCommand thunk
 */

export const copyViewToWorksheet = (store, viewId, targetWorksheetId) => {
  const payload = {
    viewId,
    targetWorksheetId
  };
  const view = getViewById(store.getState(), viewId);
  if (view.type === ViewType.MAP) {
    const currentMapExtent = loadExtentFromSession(getWorkbookIdAndHash(store.getState()).id, viewId);
    if (currentMapExtent) {
      payload.settings = { [MAP_VIEW_KEYS.INITIAL_EXTENT]: JSON.stringify(currentMapExtent) };
    }
  }
  return store.dispatch(executeCommand(Command.COPY_VIEW(payload)));
};

/**
 * Create a new worksheet
 *
 * @param {object} store - Redux store
 * @param {string} [worksheetName] - name of the new worksheet to be created
 * @return {Promise} - executeCommand thunk
 */
export const addWorksheet = (store, worksheetName) => {
  const addWorksheetCommand = Command.ADD_WORKSHEET({ worksheetName });
  return executeAddOrCopyWorksheetCommand(store, addWorksheetCommand);
};

/**
 * Creates duplicate of given worksheet
 *
 * @param {object} store - Redux store
 * @param {string} worksheetName - name of the new worksheet to be created
 * @param {import('cadenza/workbook/api/worksheet-id').WorksheetId} sourceWorksheetId - id of the
 *   source worksheet
 * @return {Promise} - executeCommand thunk
 */

export const duplicateWorksheet = (store, worksheetName, sourceWorksheetId) => {
  const copyWorksheetCommand = Command.COPY_WORKSHEET({ worksheetName, sourceWorksheetId });
  return store.dispatch(executeCommand(copyWorksheetCommand))
    .then(() => {
      const worksheets = getWorksheets(store.getState());
      // duplicated worksheet is always placed after the original
      const sourceWorksheetIndex = worksheets.findIndex(({ id }) => id === sourceWorksheetId);
      return worksheets[sourceWorksheetIndex + 1].id;
    });
};

function executeAddOrCopyWorksheetCommand (store, command) {
  return store.dispatch(executeCommand(command))
    .then(() => {
      const worksheets = getWorksheets(store.getState());
      // last one is the new worksheet
      return worksheets[worksheets.length - 1].id;
    });
}

export const removeWorksheet = (store, worksheetId) => {
  return store.dispatch(executeCommand(Command.REMOVE_WORKSHEET({ worksheetId })));
};

export const setWorksheetSettings = (store, worksheetId, settings) => {
  return store.dispatch(executeCommand(Command.CHANGE_WORKSHEET_SETTINGS({
    worksheetId,
    settings
  })));
};

export const setWorkbookSettings = (store, settings) => {
  return store.dispatch(executeCommand(Command.CHANGE_WORKBOOK_SETTINGS({ settings })));
};

/**
 * Save current extent for all map views in the workbook.
 *
 * @param {object} store - Redux store
 * @return {Promise<object>} - of the new workbook state or void, if there was no map views
 */

export const saveMapViewsExtent = (store) => {
  const workbookIdAndHash = getWorkbookIdAndHash(store.getState());
  const mapViews = getViews(store.getState()).filter((view) => view.type === ViewType.MAP);
  const commands = mapViews.flatMap(({ id: viewId }) => {
    const mapExtent = loadExtentFromSession(workbookIdAndHash.id, viewId);
    return mapExtent ? [
      Command.CHANGE_VIEW_SETTINGS({
        viewId,
        settings: { [MAP_VIEW_KEYS.INITIAL_EXTENT]: JSON.stringify(mapExtent) }
      })
    ] : [];
  });
  if (commands.length === 0) {
    return Promise.resolve();
  }
  return store.dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })));
};

export const isWorksheetOnDemandOverlayVisible = (state) =>
  isOnDemand(getActiveWorksheet(state).settings) && isActiveWorksheetDirty(state);

const shouldOnDemandBeConsidered = (state) => {
  return !isCommandOfType(lastCommand, [ Command.SET_SELECTION.type, Command.INVERT_SELECTION.type, Command.ADD_VIEW.type, Command.ADD_NEW_VIEW_WITH_DATA_VIEW.type ])
    && !isCommandOfTypeInViewType(state, lastCommand, ViewType.IMAGE, Command.CHANGE_VIEW_SETTINGS.type)
    && !isCommandOfTypeInViewType(state, lastCommand, ViewType.TEXT, Command.CHANGE_VIEW_SETTINGS.type)
    && !isHierarchyLevelChangeCommandDueToScaleChange(state, lastCommand)
    && !isSavingMapExtentCompositeCommand(state, lastCommand);
};

function isCommandOfType (command, commandTypes) {
  return command && commandTypes.includes(command.type);
}

function isCommandOfTypeInViewType (state, command, viewType, cmdType) {
  const viewId = command && command.payload?.viewId;
  const view = viewId && getViewById(state, viewId);
  return view && view.type === viewType && command.type === cmdType;
}

function isHierarchyLevelChangeCommandDueToScaleChange (state, command) {
  return command && command.type === Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION.type
    && command.payload.modificationType === AttributeModificationKey.LOCAL_HIERARCHY_LEVEL;
}

export const getCommandErrorKey = (state) => slice(state).commandErrorKey;
export const getCommandErrorDetails = (state) => slice(state).commandErrorDetail;

function isSavingMapExtentCompositeCommand (state, possibleCompositeCommand) {
  if (possibleCompositeCommand && possibleCompositeCommand.type === Command.COMPOSITE_COMMAND.type) {
    const simpleCommands = possibleCompositeCommand.payload.commands;
    return simpleCommands.length === 1 && isSavingMapExtentCommand(state, simpleCommands[0]);
  }
  return false;
}

function isSavingMapExtentCommand (state, command) {
  if (isCommandOfTypeInViewType(state, command, ViewType.MAP, Command.CHANGE_VIEW_SETTINGS.type)) {
    return command.payload.settings['map.initialExtent'];
  }
  return false;
}

function isOnDemand (settings) {
  return settings[DATA_VIEW_KEYS.COMMON_QUERY_EXECUTION_STRATEGY] === QUERY_EXECUTION_STRATEGY_TYPE.ON_DEMAND;
}

export const getOnDemandLayersIds = (state, viewId) => {
  const view = getViewById(state, viewId);
  if (view.type === ViewType.MAP) {
    return view.layers
      .filter(layer => layer.settings.dataViewId && isOnDemand(getDataViewById(state, layer.settings.dataViewId).settings))
      .map(layer => layer.id);
  }
  return [];
};

export const getLayerNameFromLayerId = (state, viewId, layerId) => {
  const view = getViewById(state, viewId);
  const layerById = view.layers.find(layer => layer.id === layerId);
  return layerById.settings.layerName;
};

export const isLayerVisible = (state, viewId, layerId) => {
  const view = getViewById(state, viewId);
  const layerById = view.layers.find(layer => layer.id === layerId);
  return layerById.settings.visible;
};

function slice (state) {
  return state[SLICE_PREFIX] ?? state;
}

function createCommandErrorDetails (serverErrorPayload) {
  if (!serverErrorPayload) {
    return null;
  }

  if (serverErrorPayload.key === WORKBOOK_ERROR_KEYS.NO_SPACE_FOR_VIEW_IN_WORKSHEET_GRID) {
    return {
      worksheetId: serverErrorPayload.detail
    };
  }
}

function updateDataViewInvalidationState (state, invalidViewsOrDataViews, dataViewsAffectedBySelectionChange) {
  state.invalidViewsOrDataViews = invalidViewsOrDataViews;
  state.dataViewsAffectedBySelectionChange = dataViewsAffectedBySelectionChange;

  if (isActiveWorksheetDirty(state) && isActiveWorksheetOnDemand(state)) {
    activateViewInternally(state, ''); // Deactivate active view
  }
}

function activateViewInternally (state, viewId) {
  if (viewId && isWorksheetOnDemandOverlayVisible(state)) {
    return; // don't activate a view when the worksheet on demand overlay is visible
  }
  state.activeViewId = viewId;
}

// Client-side state persistence

store.subscribe(() => {
  const state = slice(store.getState());
  Object.values(WorkbookPanelName).forEach(panelId => {
    cadenzaSessionStorage.setItem(panelId, state.panelById?.[panelId].visible ?? true);
  });
  cadenzaSessionStorage.setItem(WORKBOOK_CHANGED_KEY, state.clientChanged);
});

observeStore(store, getWorkbookIdAndHash, (workbookIdAndHash) => updateWorkbookUrl(workbookIdAndHash, true));
